import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
  alpha,
  useTheme,
  InputAdornment,
  Stack,
  Checkbox,
} from "@mui/material";
import { Person, Email, Phone, Lock, PersonAdd } from "@mui/icons-material";
import { validateInvitationAPI, signupWithInvitationAPI } from "../../services/user";
import { useDispatch } from "react-redux";
import { clearUserState } from "../../state/slices/user";
import validatePassword from "../../utils/passwordValidator";

export default function SignupWithInvitation() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    checked: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const isBusinessInvitation = invitation?.isBusinessInvitation;

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing invitation link.");
      setLoading(false);
      return;
    }
    validateInvitationAPI(token)
      .then((res) => {
        setInvitation(res.data);
        setFormData((f) => ({ ...f, email: res.data.email || "" }));
      })
      .catch((e) => setError(e.response?.data?.msg || "Invalid or expired invitation."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
    validateField(e.target.name, value);
  };

  const validateField = (fieldName, value) => {
    const validationErrors = { ...errors };
    switch (fieldName) {
      case "firstName":
        validationErrors.firstName = value.trim()
          ? String(value).length < 3
            ? t('validation.firstNameTooShort')
            : ""
          : t('validation.firstNameRequired');
        break;
      case "lastName":
        validationErrors.lastName = value.trim()
          ? String(value).length < 3
            ? t('validation.lastNameTooShort')
            : ""
          : t('validation.lastNameRequired');
        break;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        validationErrors.email =
          value.trim() && emailRegex.test(value) ? "" : t('validation.invalidEmail');
        break;
      case "phoneNumber":
        const phoneRegex = /^\+251\d{9}$/;
        validationErrors.phoneNumber =
          value.trim() && phoneRegex.test(value) ? "" : t('validation.phoneNumberInvalid');
        break;
      case "password":
        validationErrors.password = value.trim()
          ? validatePassword(String(value))
          : t('validation.passwordRequired');
        break;
      case "confirmPassword":
        validationErrors.confirmPassword =
          value === formData.password ? "" : t('validation.passwordsDoNotMatch');
        break;
      case "checked":
        validationErrors.checked = value
          ? ""
          : t('validation.termsRequired');
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, ...validationErrors }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = {};
    Object.keys(formData).forEach((fieldName) => {
      validateField(fieldName, formData[fieldName]);
      if (errors[fieldName]) {
        validationErrors[fieldName] = errors[fieldName];
      }
    });
    setSubmitStatus((prev) => prev + "x");
  };

  useEffect(() => {
    if (
      submitStatus &&
      Object.keys(errors).filter((key) => errors[key]).length === 0
    ) {
      setSubmitting(true);
      setError("");
      // Only send fields that the backend expects (exclude confirmPassword and checked)
      const { confirmPassword, checked, ...dataToSend } = formData;
      signupWithInvitationAPI({ token, ...dataToSend })
        .then(() => {
          dispatch(clearUserState());
          navigate("/login", { state: { message: "Account created. Please log in." } });
        })
        .catch((e) => {
          setError(e.response?.data?.msg || e.message);
        })
        .finally(() => {
          setSubmitting(false);
          setSubmitStatus("");
        });
    } else {
      dispatch(clearUserState());
    }
  }, [submitStatus, token, formData, errors, dispatch, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!token || !invitation) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={0} alignItems="stretch">
          {/* Left Image Section */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                minHeight: '550px',
                borderRadius: '4px 0 0 4px',
                overflow: 'hidden',
                boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <img
                src={isBusinessInvitation ? `/business-education3.jpg` : `/PARENT.jpg`}
                alt="Sign Up"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <PersonAdd sx={{ fontSize: 50, opacity: 0.95 }} />
                    <Box
                      sx={{
                        px: 3,
                        py: 1,
                        borderRadius: 3,
                        bgcolor: alpha('#ffffff', 0.2),
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {isBusinessInvitation
                          ? t('signup.forEmployees') || 'For Employees'
                          : t('signup.forChildren') || 'For Children'}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    {t('signup.joinToday') || 'Join Today'}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95, lineHeight: 1.8, fontSize: '1.1rem' }}>
                    {isBusinessInvitation
                      ? t('signup.employeeSubtitle') || 'You\'ve been invited to join Astegne as an employee. Complete your account setup to get started with quality tutoring.'
                      : t('signup.childSubtitle') || 'You\'ve been invited to join Astegne. Complete your account setup to get started with quality tutoring.'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Signup Form */}
          <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '0 4px 4px 0',
                p: { xs: 3, md: 4 },
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  {t('signup.createAccount') || 'Create Your Account'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  {isBusinessInvitation
                    ? t('signup.employeeInvitationMessage') || 'You\'ve been invited to join as an employee. Complete the form below to create your account.'
                    : t('signup.childInvitationMessage') || 'You\'ve been invited to join Astegne. Complete the form below to create your account.'}
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError("")}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingRight: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                      <TextField
                        label={t('signup.firstName')}
                        variant="outlined"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: '100%',
                          boxSizing: 'border-box',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              }
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingLeft: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                      <TextField
                        label={t('signup.lastName')}
                        variant="outlined"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: '100%',
                          boxSizing: 'border-box',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              }
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                            }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    label={t('common.email')}
                    variant="outlined"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email || (t('signup.emailHelper') || 'You can use your own email address')}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&.Mui-focused': {
                          bgcolor: 'white',
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                        }
                      }
                    }}
                  />

                  <TextField
                    label={t('signup.phoneNumber')}
                    variant="outlined"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&.Mui-focused': {
                          bgcolor: 'white',
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                        }
                      }
                    }}
                  />

                  <TextField
                    label={t('common.password')}
                    variant="outlined"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&.Mui-focused': {
                          bgcolor: 'white',
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                        }
                      }
                    }}
                  />

                  <TextField
                    label={t('signup.confirmPassword')}
                    variant="outlined"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&.Mui-focused': {
                          bgcolor: 'white',
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                        }
                      }
                    }}
                  />

                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Checkbox
                      name="checked"
                      checked={formData.checked}
                      onChange={handleChange}
                      sx={{ mt: -1 }}
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('signup.agreeTerms')}{" "}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.palette.primary.main }}
                      >
                        {t('signup.termsAndConditions')}
                      </Link>
                    </Typography>
                  </Box>
                  {errors.checked && (
                    <Typography variant="caption" color="error" sx={{ ml: 4, mt: -2 }}>
                      {errors.checked}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={submitting}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: theme.palette.secondary.main,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
                      }
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={25} color="inherit" />
                    ) : (
                      t('signup.createAccount') || 'Create Account'
                    )}
                  </Button>

                  <Box sx={{ textAlign: "center", mt: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {t('signup.alreadyHaveAccount')}{" "}
                      <Link to="/login" style={{ color: theme.palette.primary.main, fontWeight: 700, textDecoration: "none" }}>
                        {t('signup.login')}
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              </form>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
